import React, { useMemo, useRef, useState, useCallback, useLayoutEffect, useEffect } from 'react'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted'

const PADDING_X = 60
const PADDING_TOP = 40
const PADDING_BOTTOM = 60
const CALLOUT_WIDTH = 196
const PERFORATION_PANEL_KEY = 'perforation-panel'
const PERFORATION_PANEL_WIDTH = CALLOUT_WIDTH + 20
const PERFORATION_PANEL_HEIGHT = 14 + 5 * 15
// space kept free between callouts and above the legend when they are laid out
const CALLOUT_GAP = 8
// default callouts keep this distance from their anchor point and this clearance around the pipe
const CALLOUT_ANCHOR_DISTANCE = 40
const CALLOUT_PIPE_CLEARANCE = 16
const LEGEND_SPACE = 40
// compact (mobile): narrower side paddings, height follows the well instead of the container
const COMPACT_PADDING_LEFT = 40
const COMPACT_PADDING_RIGHT = 28
const COMPACT_MAX_HEIGHT = 520
const COMPACT_MIN_HEIGHT = 220

const PALETTES = {
    light: {
        bgFrom: '#fbfcfd',
        bgTo: '#f1f3f5',
        gridLine: '#e3e6ea',
        gridText: '#a3aab1',
        tubingBase: '#a3a9b0',
        tubingHighlight: '#d4d8dc',
        perforatedBase: '#a63a3a',
        perforatedHighlight: '#d97b7b',
        markPassed: '#5b6169',
        markUpcoming: '#5c1c1a',
        // live fluid colours are desaturated and kept in this lightness range so they are not too bright
        fluidSaturation: 0.62,
        fluidLightness: [0.52, 0.66],
        connectorLine: '#a9afb5',
        pointFill: '#ffffff',
        pointStroke: '#6b7178',
        calloutBg: '#ffffff',
        calloutBorder: '#e1e4e8',
        calloutBorderActive: '#8b9198',
        calloutTitle: '#37474f',
        calloutText: '#78838d',
        calloutAccentDefault: '#6b7178',
        calloutAccentActive: '#a63a3a',
        shadowColor: '#1a1a1a',
        shadowOpacity: 0.14,
        legendText: '#78838d',
        badgeBg: '#37474f',
        badgeText: '#ffffff',
        badgeSelectedRing: '#006ae7',
        emptyText: '#9aa0a6',
        emptyBorder: '#dcdfe3',
    },
    dark: {
        bgFrom: '#262626',
        bgTo: '#1c1c1c',
        gridLine: 'rgba(255, 255, 255, 0.1)',
        gridText: 'rgba(255, 255, 255, 0.4)',
        tubingBase: '#6c737b',
        tubingHighlight: '#8d949c',
        perforatedBase: '#c1544e',
        perforatedHighlight: '#e08b85',
        markPassed: '#9aa2ab',
        markUpcoming: '#7a2b26',
        fluidSaturation: 0.62,
        fluidLightness: [0.34, 0.5],
        connectorLine: 'rgba(255, 255, 255, 0.28)',
        pointFill: '#2c2c2c',
        pointStroke: 'rgba(255, 255, 255, 0.56)',
        calloutBg: '#2c2c2c',
        calloutBorder: 'rgba(255, 255, 255, 0.14)',
        calloutBorderActive: 'rgba(255, 255, 255, 0.4)',
        calloutTitle: 'rgba(255, 255, 255, 0.92)',
        calloutText: 'rgba(255, 255, 255, 0.6)',
        calloutAccentDefault: 'rgba(255, 255, 255, 0.4)',
        calloutAccentActive: '#c1544e',
        shadowColor: '#000000',
        shadowOpacity: 0.5,
        legendText: 'rgba(255, 255, 255, 0.6)',
        badgeBg: 'rgba(255, 255, 255, 0.86)',
        badgeText: '#1c1c1c',
        badgeSelectedRing: '#0094c2',
        emptyText: 'rgba(255, 255, 255, 0.4)',
        emptyBorder: 'rgba(255, 255, 255, 0.16)',
    },
}

// Reconstructs a "vertical section" style 2D trajectory: TVD (given directly by
// the survey data) is used as-is for depth, and the horizontal position is
// derived from MD and TVD via Pythagoras (horizontal² = MD² − ΔTVD²) rather
// than from the raw NS/EW magnitude. This guarantees that every drawn
// segment's on-screen (Euclidean) length is exactly proportional to its real
// MD length, regardless of how the well's azimuth happens to turn between
// survey stations — which raw NS/EW would not guarantee.
const withHorizontalPositions = (sortedPoints) => {
    let cumulativeHorizontal = 0
    return sortedPoints.map((p, i) => {
        if (i === 0) return { ...p, horizontal: 0 }
        const prev = sortedPoints[i - 1]
        const mdDelta = p.md - prev.md
        const tvdDelta = (p.tvd || 0) - (prev.tvd || 0)
        const horizontalDelta = Math.sqrt(Math.max(mdDelta * mdDelta - tvdDelta * tvdDelta, 0))
        cumulativeHorizontal += horizontalDelta
        return { ...p, horizontal: cumulativeHorizontal }
    })
}

const findTubularAt = (tubularData, md) => {
    return tubularData.find(t => md >= t.topMD && md <= t.bottomMD)
}

const isPointInInjectionZone = (injectionPoints, md) => {
    return injectionPoints.some(p => md >= p.topMD && md <= p.bottomMD)
}

const scaleDiameter = (od) => {
    if (!od) return 8
    const px = od * 240
    return Math.min(Math.max(px, 6), 30)
}

const round = (n, digits = 1) => {
    const factor = Math.pow(10, digits);
    return Math.round((n + Number.EPSILON) * factor) / factor;
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

// '#rgb', '#rrggbb' or 'rgb(a)(r, g, b...)' -> [r, g, b], null for anything else
const parseColor = (color) => {
    if (typeof color !== 'string') return null
    const value = color.trim()
    const hex = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)
    if (hex) {
        const h = hex[1].length === 3 ? hex[1].split('').map(c => c + c).join('') : hex[1]
        return [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16))
    }
    const rgb = value.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
    return rgb ? rgb.slice(1, 4).map(Number) : null
}

const rgbToHsl = ([r, g, b]) => {
    const [rn, gn, bn] = [r / 255, g / 255, b / 255]
    const max = Math.max(rn, gn, bn)
    const min = Math.min(rn, gn, bn)
    const l = (max + min) / 2
    if (max === min) return [0, 0, l]
    const d = max - min
    const sat = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    let h
    if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0)
    else if (max === gn) h = (bn - rn) / d + 2
    else h = (rn - gn) / d + 4
    return [h * 60, sat, l]
}

const hslToRgb = ([h, sat, l]) => {
    const c = (1 - Math.abs(2 * l - 1)) * sat
    const x = c * (1 - Math.abs((h / 60) % 2 - 1))
    const m = l - c / 2
    const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x]
        : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x]
    return [r, g, b].map(v => Math.round((v + m) * 255))
}

// scales the saturation and clamps the lightness, unknown formats are returned as is
const softenColor = (color, saturation, [minLightness, maxLightness]) => {
    const rgb = parseColor(color)
    if (!rgb) return color
    const [h, sat, l] = rgbToHsl(rgb)
    const [r, g, b] = hslToRgb([h, sat * saturation, clamp(l, minLightness, maxLightness)])
    return `rgb(${r}, ${g}, ${b})`
}

const getPositionAtMd = (points, md) => {
    if (!points.length) return null
    if (md <= points[0].md) {
        const a = points[0]
        const b = points[1] || points[0]
        return { x: a.x, y: a.y, angle: Math.atan2(b.y - a.y, b.x - a.x) }
    }
    const lastIdx = points.length - 1
    if (md >= points[lastIdx].md) {
        const a = points[Math.max(lastIdx - 1, 0)]
        const b = points[lastIdx]
        return { x: b.x, y: b.y, angle: Math.atan2(b.y - a.y, b.x - a.x) }
    }
    for (let i = 0; i < lastIdx; i++) {
        const a = points[i]
        const b = points[i + 1]
        if (md >= a.md && md <= b.md) {
            const t = (b.md - a.md) === 0 ? 0 : (md - a.md) / (b.md - a.md)
            return {
                x: a.x + (b.x - a.x) * t,
                y: a.y + (b.y - a.y) * t,
                angle: Math.atan2(b.y - a.y, b.x - a.x),
            }
        }
    }
    return null
}

const closestPointOnRect = (anchor, rect) => ({
    x: clamp(anchor.x, rect.x, rect.x + rect.width),
    y: clamp(anchor.y, rect.y, rect.y + rect.height),
})

const getPointCalloutHeight = (tubularData, point) => 14 + (findTubularAt(tubularData, point.md) ? 3 : 1) * 16

// overlap area of two rects, each one grown by gap / 2
const getOverlapArea = (a, b, gap) => {
    const w = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x) + gap
    const h = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y) + gap
    return w > 0 && h > 0 ? w * h : 0
}

// fluidSegments != null (Data tab) only adds live fluid colours on top of the regular pipe.
// compact: callouts are replaced with numbered badges and a details list under the diagram (mobile).
const WellborePipeDiagram = ({ deviationData = [], tubularData = [], injectionPoints = [], activeInjectionPoint = null, fluidSegments = null, theme, compact = false }) => {
    const isDataMode = fluidSegments != null

    const colors = theme === 'Dark' ? PALETTES.dark : PALETTES.light

    const wrapperRef = useRef(null)
    const svgRef = useRef(null)
    const dragRef = useRef(null)
    const [size, setSize] = useState({ width: 520, height: 680 })
    const [positions, setPositions] = useState({})
    const [draggingKey, setDraggingKey] = useState(null)
    // compact mode: details under the diagram are hidden by default; a badge click opens just its card
    const [showAllDetails, setShowAllDetails] = useState(false)
    const [selectedDetail, setSelectedDetail] = useState(null)
    const detailRefs = useRef({})

    // with all details shown, a badge click scrolls to its card
    useEffect(() => {
        if (!compact || !showAllDetails || selectedDetail == null) return
        detailRefs.current[selectedDetail]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, [compact, showAllDetails, selectedDetail])
    const { width, height } = size
    const paddingLeft = compact ? COMPACT_PADDING_LEFT : PADDING_X
    const paddingRight = compact ? COMPACT_PADDING_RIGHT : PADDING_X

    useLayoutEffect(() => {
        const el = wrapperRef.current
        if (!el || typeof ResizeObserver === 'undefined') return undefined
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                const { width: w, height: h } = entry.contentRect
                if (w > 0 && h > 0) {
                    setSize({ width: w, height: h })
                }
            }
        })
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    const activeZonePoints = useMemo(
        () => (activeInjectionPoint ? [activeInjectionPoint] : []),
        [activeInjectionPoint],
    )

    const cutoffMd = useMemo(
        () => (activeInjectionPoint ? Math.min(activeInjectionPoint.topMD, activeInjectionPoint.bottomMD) : null),
        [activeInjectionPoint],
    )

    const sortedWithHorizontal = useMemo(() => {
        const sorted = [...deviationData].sort((a, b) => a.md - b.md)
        return withHorizontalPositions(sorted)
    }, [deviationData])

    // A single scale for both axes so real-world distances (metres) map to the
    // same number of pixels horizontally and vertically — otherwise each axis
    // stretches independently to fill its own space and segment lengths stop
    // being proportional to their actual measured length.
    const scale = useMemo(() => {
        if (!sortedWithHorizontal.length) return 1
        const maxH = Math.max(1, ...sortedWithHorizontal.map(p => p.horizontal))
        const maxV = Math.max(1, ...sortedWithHorizontal.map(p => p.tvd || 0))
        const availableW = Math.max(width - paddingLeft - paddingRight, 1)
        const availableH = Math.max((compact ? COMPACT_MAX_HEIGHT : height) - PADDING_TOP - PADDING_BOTTOM, 1)
        return Math.min(availableW / maxH, availableH / maxV)
    }, [sortedWithHorizontal, width, height, paddingLeft, paddingRight, compact])

    const svgHeight = useMemo(() => {
        if (!compact) return height
        const maxV = Math.max(1, ...sortedWithHorizontal.map(p => p.tvd || 0))
        return Math.max(PADDING_TOP + maxV * scale + PADDING_BOTTOM, COMPACT_MIN_HEIGHT)
    }, [compact, height, sortedWithHorizontal, scale])

    const points = useMemo(() => {
        return sortedWithHorizontal.map(p => ({
            ...p,
            x: paddingLeft + p.horizontal * scale,
            y: PADDING_TOP + (p.tvd || 0) * scale,
        }))
    }, [sortedWithHorizontal, scale, paddingLeft])

    const depthTicks = useMemo(() => {
        if (!points.length) return []
        const maxV = Math.max(1, ...points.map(p => p.tvd || 0))
        const tickCount = 5
        return Array.from({ length: tickCount + 1 }, (_, i) => {
            const value = (maxV / tickCount) * i
            return {
                value,
                y: PADDING_TOP + value * scale,
            }
        })
    }, [points, scale])

    const handlePointerDown = useCallback((key, e, currentPos) => {
        e.stopPropagation()
        const ctm = svgRef.current.getScreenCTM()
        const rect = svgRef.current.getBoundingClientRect()
        dragRef.current = {
            key,
            startMouseX: e.clientX,
            startMouseY: e.clientY,
            startBoxX: currentPos.x,
            startBoxY: currentPos.y,
            scaleX: ctm ? 1 / ctm.a : width / rect.width,
            scaleY: ctm ? 1 / ctm.d : svgHeight / rect.height,
        }
        setDraggingKey(key)
        e.target.setPointerCapture(e.pointerId)
    }, [width, svgHeight])

    const handlePointerMove = useCallback((e) => {
        const d = dragRef.current
        if (!d) return
        const dx = (e.clientX - d.startMouseX) * d.scaleX
        const dy = (e.clientY - d.startMouseY) * d.scaleY
        setPositions(prev => ({
            ...prev,
            [d.key]: { x: d.startBoxX + dx, y: d.startBoxY + dy },
        }))
    }, [])

    const handlePointerUp = useCallback((e) => {
        if (dragRef.current && e.target.releasePointerCapture) {
            try {
                e.target.releasePointerCapture(e.pointerId)
            } catch (err) {
                // ignore
            }
        }
        dragRef.current = null
        setDraggingKey(null)
    }, [])

    const perforationMarks = useMemo(() => {
        const marks = []
        injectionPoints.forEach((zone) => {
            const topMd = Math.min(zone.topMD, zone.bottomMD)
            const bottomMd = Math.max(zone.topMD, zone.bottomMD)
            const span = Math.max(bottomMd - topMd, 1)
            const markCount = Math.max(3, Math.min(6, Math.round(span / 15)))
            const isPassed = activeInjectionPoint != null && zone.zone <= activeInjectionPoint.zone
            for (let i = 0; i < markCount; i++) {
                const t = markCount === 1 ? 0.5 : i / (markCount - 1)
                const md = topMd + span * t
                const pos = getPositionAtMd(points, md)
                if (!pos) continue
                const tubular = findTubularAt(tubularData, md)
                const halfLength = scaleDiameter(tubular?.outerDiameter) * 0.7 + 3
                marks.push({
                    key: `zone-${zone.zone}-${i}`,
                    x: pos.x,
                    y: pos.y,
                    angle: pos.angle,
                    halfLength,
                    color: isPassed ? colors.markPassed : colors.markUpcoming,
                })
            }
        })
        return marks
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [injectionPoints, activeInjectionPoint, points, tubularData, colors.markPassed, colors.markUpcoming])

    const perforationAnchors = useMemo(() => {
        return activeZonePoints.map((zone, idx) => {
            const topMd = Math.min(zone.topMD, zone.bottomMD)
            const bottomMd = Math.max(zone.topMD, zone.bottomMD)
            const pos = getPositionAtMd(points, (topMd + bottomMd) / 2)
            if (!pos) return null
            return { ...pos, zone, idx }
        }).filter(Boolean)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeZonePoints, points])

    // Default callout positions: each callout takes the closest spot around its anchor that does not overlap
    // the callouts placed before it and, if possible, does not cover the pipe. Dragged callouts keep their own spot.
    const calloutLayout = useMemo(() => {
        if (compact || !points.length) return {}

        const bottomLimit = svgHeight - LEGEND_SPACE
        const pipeSamples = []
        for (let i = 0; i < points.length - 1; i++) {
            const a = points[i]
            const b = points[i + 1]
            const radius = scaleDiameter(findTubularAt(tubularData, (a.md + b.md) / 2)?.outerDiameter) / 2 + CALLOUT_PIPE_CLEARANCE
            const steps = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / 6))
            for (let step = 0; step <= steps; step++) {
                const t = step / steps
                pipeSamples.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, radius })
            }
        }
        const countPipeHits = rect => pipeSamples.filter(p => (
            p.x > rect.x - p.radius && p.x < rect.x + rect.width + p.radius
            && p.y > rect.y - p.radius && p.y < rect.y + rect.height + p.radius
        )).length

        const placed = []
        const layout = {}
        const place = (key, anchor, boxWidth, boxHeight) => {
            // next to the anchor on both sides, further away, and pressed to the diagram edges
            const xs = [
                ...[CALLOUT_ANCHOR_DISTANCE, 70, 120].flatMap(offset => [anchor.x + offset, anchor.x - offset - boxWidth]),
                4,
                width - 4 - boxWidth,
            ]
            const yStep = boxHeight / 3
            const ys = Array.from({ length: 31 }, (_, i) => anchor.y - boxHeight / 2 + (i - 15) * yStep)
            let best = null
            xs.forEach(x => ys.forEach(y => {
                if (x < 4 || x + boxWidth > width - 4 || y < 4 || y + boxHeight > bottomLimit) return
                const rect = { x, y, width: boxWidth, height: boxHeight }
                const overlap = placed.reduce((sum, other) => sum + getOverlapArea(rect, other, CALLOUT_GAP), 0)
                const attach = closestPointOnRect(anchor, rect)
                const distance = Math.hypot(attach.x - anchor.x, attach.y - anchor.y)
                // closer than the preferred distance counts double, so callouts don't hug the pipe
                const distanceCost = distance < CALLOUT_ANCHOR_DISTANCE
                    ? (CALLOUT_ANCHOR_DISTANCE - distance) * 2
                    : distance - CALLOUT_ANCHOR_DISTANCE
                // any overlap with another callout is worse than covering the pipe or being far from the anchor
                const cost = (overlap > 0 ? 1e6 + overlap : 0) + countPipeHits(rect) * 150 + distanceCost
                if (!best || cost < best.cost) best = { rect, cost }
            }))
            const rect = best ? best.rect : {
                x: clamp(anchor.x + CALLOUT_ANCHOR_DISTANCE, 4, Math.max(width - boxWidth - 4, 4)),
                y: clamp(anchor.y - boxHeight / 2, 4, Math.max(bottomLimit - boxHeight, 4)),
                width: boxWidth,
                height: boxHeight,
            }
            placed.push(rect)
            layout[key] = { x: rect.x, y: rect.y }
        }

        points.slice(0, -1).forEach((point, idx) => {
            place(`point-${idx}`, point, CALLOUT_WIDTH, getPointCalloutHeight(tubularData, point))
        })
        perforationAnchors.forEach(anchor => place(PERFORATION_PANEL_KEY, anchor, PERFORATION_PANEL_WIDTH, PERFORATION_PANEL_HEIGHT))

        return layout
    }, [compact, points, perforationAnchors, tubularData, width, svgHeight])

    if (!points.length) {
        return (
            <div ref={wrapperRef} style={{ width: '100%', height: '100%', boxSizing: 'border-box' }}>
                <div style={{
                    padding: 32,
                    textAlign: 'center',
                    color: colors.emptyText,
                    border: `1px dashed ${colors.emptyBorder}`,
                    borderRadius: 10,
                }}>
                    No deviation data to display.
                </div>
            </div>
        )
    }

    const segments = points.slice(1).flatMap((point, idx) => {
        const prev = points[idx]

        const buildPiece = (x1, y1, md1, x2, y2, md2, isPerforated, keySuffix, cap) => {
            const tubular = findTubularAt(tubularData, (md1 + md2) / 2)
            const strokeWidth = scaleDiameter(tubular?.outerDiameter)

            const dx = x2 - x1
            const dy = y2 - y1
            const len = Math.max(Math.hypot(dx, dy), 0.0001)
            const nx = -dy / len
            const ny = dx / len
            const offset = strokeWidth * 0.16

            // Perforated (stage) intervals keep their own colour on both tabs
            const showPerforatedColor = isPerforated

            return {
                key: `${prev.md}-${point.md}-${keySuffix}`,
                x1, y1, x2, y2,
                md1, md2,
                strokeWidth,
                cap,
                isPerforated,
                baseColor: showPerforatedColor ? colors.perforatedBase : colors.tubingBase,
                highlightColor: showPerforatedColor ? colors.perforatedHighlight : colors.tubingHighlight,
                hx1: x1 + nx * offset,
                hy1: y1 + ny * offset,
                hx2: x2 + nx * offset,
                hy2: y2 + ny * offset,
            }
        }

        // Cutoff falls entirely before or after this segment — render it as a single piece
        // with round caps so it blends smoothly into its neighbouring bend joints.
        if (cutoffMd == null || cutoffMd <= prev.md) {
            return [buildPiece(prev.x, prev.y, prev.md, point.x, point.y, point.md, cutoffMd != null, 'full', 'round')]
        }
        if (cutoffMd >= point.md) {
            return [buildPiece(prev.x, prev.y, prev.md, point.x, point.y, point.md, false, 'full', 'round')]
        }

        // Cutoff falls inside this segment — split it exactly at that depth so the
        // gray/red boundary lines up with the active zone regardless of how sparse
        // the raw deviation points are. Both pieces use a flat ("butt") cap: they are
        // colinear, so the flat ends meet without a gap, and — unlike a round cap —
        // neither piece bulges past the split point into the other's colour.
        const t = (cutoffMd - prev.md) / (point.md - prev.md)
        const splitX = prev.x + (point.x - prev.x) * t
        const splitY = prev.y + (point.y - prev.y) * t

        return [
            buildPiece(prev.x, prev.y, prev.md, splitX, splitY, cutoffMd, false, 'gray', 'butt'),
            buildPiece(splitX, splitY, cutoffMd, point.x, point.y, point.md, true, 'red', 'butt'),
        ]
    })

    // Live fluid is laid over the same pipe pieces and takes their width minus 3px, so the pipe shows as an
    // even outline around it everywhere (a separate tubular lookup for the fluid could pick another diameter).
    const fluidPieces = !isDataMode ? [] : segments.flatMap(seg => fluidSegments.map((fluid, fluidIdx) => {
        const from = Math.max(Math.min(fluid.topMD, fluid.bottomMD), seg.md1)
        const to = Math.min(Math.max(fluid.topMD, fluid.bottomMD), seg.md2)
        if (to <= from) return null
        const span = seg.md2 - seg.md1
        const t1 = span ? (from - seg.md1) / span : 0
        const t2 = span ? (to - seg.md1) / span : 1
        return {
            key: `fluid-${fluidIdx}-${seg.key}`,
            x1: seg.x1 + (seg.x2 - seg.x1) * t1,
            y1: seg.y1 + (seg.y2 - seg.y1) * t1,
            x2: seg.x1 + (seg.x2 - seg.x1) * t2,
            y2: seg.y1 + (seg.y2 - seg.y1) * t2,
            strokeWidth: Math.max(seg.strokeWidth - 3, 3),
            color: softenColor(fluid.color, colors.fluidSaturation, colors.fluidLightness),
        }
    }).filter(Boolean))

    const renderCallout = (point, idx) => {
        if (!point) return null
        const key = `point-${idx}`
        const accentColor = isPointInInjectionZone(activeZonePoints, point.md) ? colors.calloutAccentActive : colors.calloutAccentDefault
        const tubular = findTubularAt(tubularData, point.md)
        const boxHeight = getPointCalloutHeight(tubularData, point)

        const position = positions[key] || calloutLayout[key] || { x: point.x + 14, y: point.y - boxHeight / 2 }
        const boxX = position.x
        const boxY = position.y

        const attach = closestPointOnRect(point, { x: boxX, y: boxY, width: CALLOUT_WIDTH, height: boxHeight })
        const isDragging = draggingKey === key

        return (
            <g key={key}>
                <line
                    x1={point.x} y1={point.y}
                    x2={attach.x} y2={attach.y}
                    stroke={colors.connectorLine}
                    strokeWidth="1"
                    strokeDasharray="3 3"
                />
                <g
                    transform={`translate(${boxX}, ${boxY})`}
                    onPointerDown={(e) => handlePointerDown(key, e, { x: boxX, y: boxY })}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
                >
                    <rect
                        x="0" y="0" width={CALLOUT_WIDTH} height={boxHeight}
                        rx="6" ry="6"
                        fill={colors.calloutBg}
                        stroke={isDragging ? colors.calloutBorderActive : colors.calloutBorder}
                        strokeWidth="1"
                        filter="url(#calloutShadow)"
                    />
                    <rect x="0" y="0" width="4" height={boxHeight} rx="2" fill={accentColor} />
                    <text x="14" y="17" fontSize="10.5" fontFamily="'Roboto Mono', monospace" fontWeight="600" fill={colors.calloutTitle}>
                        {`MD/TVD ${Math.round(point.md)} / ${Math.round(point.tvd || 0)} m`}
                    </text>
                    {
                        tubular && (
                            <text x="14" y="33" fontSize="10.5" fontFamily="'Roboto Mono', monospace" fill={colors.calloutText}>
                                {`Tubing ID/OD ${round(tubular.innerDiameter, 3)} / ${round(tubular.outerDiameter, 3)} m`}
                            </text>
                        )
                    }
                    {
                        tubular && (
                            <text x="14" y="49" fontSize="10.5" fontFamily="'Roboto Mono', monospace" fill={colors.calloutText}>
                                {`Casing ID/OD ${round(tubular.casingInnerDiameter, 3)} / ${round(tubular.casingOuterDiameter, 3)} m`}
                            </text>
                        )
                    }
                </g>
            </g>
        )
    }

    const toggleDetail = (key) => setSelectedDetail(prev => (prev === key ? null : key))

    // compact mode: a clickable badge; selected one gets a ring
    const renderBadgeMark = ({ key, x, y, label, isActive }) => {
        const isSelected = selectedDetail === key
        return (
            <g
                key={`badge-${key}`}
                transform={`translate(${x}, ${y})`}
                className="wellboreDiagramBadge"
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                onClick={() => toggleDetail(key)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        toggleDetail(key)
                    }
                }}
                style={{ cursor: 'pointer' }}
            >
                {/* larger transparent hit area for fingers */}
                <circle r="16" fill="transparent" />
                {isSelected && <circle r="13" fill="none" stroke={colors.badgeSelectedRing} strokeWidth="2.5" />}
                <circle r={isSelected ? 10 : 9} fill={isActive ? colors.calloutAccentActive : colors.badgeBg} />
                <text y="3.5" textAnchor="middle" fontSize="10" fontWeight="600" fill={isActive ? '#ffffff' : colors.badgeText}>
                    {label}
                </text>
            </g>
        )
    }

    // a numbered badge next to the point, away from the pipe
    const renderBadge = (point, idx) => {
        const next = points[idx + 1] || point
        const dx = next.x - point.x
        const dy = next.y - point.y
        const len = Math.hypot(dx, dy)
        // normal to the pipe pointing right / up, straight right when the next point is the same
        const nx = len ? dy / len : 1
        const ny = len ? -dx / len : 0
        const tubular = findTubularAt(tubularData, point.md)
        const offset = scaleDiameter(tubular?.outerDiameter) / 2 + 13
        return renderBadgeMark({
            key: idx,
            x: point.x + nx * offset,
            y: point.y + ny * offset,
            label: idx + 1,
            isActive: isPointInInjectionZone(activeZonePoints, point.md),
        })
    }

    // perforation badge above the middle of the active zone
    const renderPerforationBadge = (anchor) => {
        const tubular = findTubularAt(tubularData, anchor.zone.topMD)
        const offset = scaleDiameter(tubular?.outerDiameter) / 2 + 13
        return renderBadgeMark({ key: PERFORATION_PANEL_KEY, x: anchor.x, y: anchor.y - offset, label: 'P', isActive: true })
    }

    const isDetailVisible = (key) => showAllDetails || selectedDetail === key

    const renderDetails = () => (
        <div className="wellboreDiagramDetails">
            {
                points.slice(0, -1).map((point, idx) => {
                    if (!isDetailVisible(idx)) return null
                    const tubular = findTubularAt(tubularData, point.md)
                    const isActive = isPointInInjectionZone(activeZonePoints, point.md)
                    return (
                        <div
                            key={idx}
                            ref={el => { detailRefs.current[idx] = el }}
                            className={`wellboreDiagramDetail${isActive ? ' active' : ''}${selectedDetail === idx ? ' selected' : ''}`}
                        >
                            <span className="wellboreDiagramDetailBadge">{idx + 1}</span>
                            <div className="wellboreDiagramDetailText">
                                <div className="wellboreDiagramDetailTitle">
                                    {`MD/TVD ${Math.round(point.md)} / ${Math.round(point.tvd || 0)} m`}
                                </div>
                                {
                                    tubular && (
                                        <div className="wellboreDiagramDetailMeta">
                                            {`Tubing ID/OD ${round(tubular.innerDiameter, 3)} / ${round(tubular.outerDiameter, 3)} m`}
                                            <br />
                                            {`Casing ID/OD ${round(tubular.casingInnerDiameter, 3)} / ${round(tubular.casingOuterDiameter, 3)} m`}
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                    )
                })
            }
            {
                perforationAnchors.filter(() => isDetailVisible(PERFORATION_PANEL_KEY)).map(anchor => (
                    <div
                        key={PERFORATION_PANEL_KEY}
                        ref={el => { detailRefs.current[PERFORATION_PANEL_KEY] = el }}
                        className={`wellboreDiagramDetail perforation${selectedDetail === PERFORATION_PANEL_KEY ? ' selected' : ''}`}
                    >
                        <span className="wellboreDiagramDetailBadge">P</span>
                        <div className="wellboreDiagramDetailText">
                            <div className="wellboreDiagramDetailTitle">{`Perforation · Stage ${anchor.zone.zone}`}</div>
                            <div className="wellboreDiagramDetailMeta">
                                {`Top/Bottom (MD) ${round(anchor.zone.topMD, 1)} / ${round(anchor.zone.bottomMD, 1)} m`}
                                <br />
                                {`Nr of perf. ${anchor.zone.nrOfPerforation ?? 0} · Diameter ${round(anchor.zone.perforationDiameter ?? 0, 3)} m`}
                            </div>
                        </div>
                    </div>
                ))
            }
        </div>
    )

    const renderPerforationCallout = (anchor) => {
        const key = PERFORATION_PANEL_KEY
        const panelWidth = PERFORATION_PANEL_WIDTH
        const boxHeight = PERFORATION_PANEL_HEIGHT
        const defaultPosition = calloutLayout[key] || { x: anchor.x + 14, y: anchor.y - boxHeight / 2 }
        // Once the user has dragged this panel, keep it pinned to that same absolute
        // position when switching stages — only the connector line below re-points
        // to the newly active stage's anchor.
        const stored = positions[key]
        const boxX = stored ? stored.x : defaultPosition.x
        const boxY = stored ? stored.y : defaultPosition.y

        const attach = closestPointOnRect(anchor, { x: boxX, y: boxY, width: panelWidth, height: boxHeight })
        const isDragging = draggingKey === key

        return (
            <g key={key}>
                <line
                    x1={anchor.x} y1={anchor.y}
                    x2={attach.x} y2={attach.y}
                    stroke={colors.connectorLine}
                    strokeWidth="1"
                    strokeDasharray="3 3"
                />
                <g
                    transform={`translate(${boxX}, ${boxY})`}
                    onPointerDown={(e) => handlePointerDown(key, e, { x: boxX, y: boxY })}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
                >
                    <rect
                        x="0" y="0" width={panelWidth} height={boxHeight}
                        rx="6" ry="6"
                        fill={colors.calloutBg}
                        stroke={isDragging ? colors.calloutBorderActive : colors.calloutBorder}
                        strokeWidth="1"
                        filter="url(#calloutShadow)"
                    />
                    <rect x="0" y="0" width="4" height={boxHeight} rx="2" fill={colors.calloutAccentActive} />
                    <text x="14" y="16" fontSize="10.5" fontFamily="'Roboto Mono', monospace" fontWeight="600" fill={colors.calloutTitle}>
                        Perforation
                    </text>
                    <text x="14" y="31" fontSize="10.5" fontFamily="'Roboto Mono', monospace" fill={colors.calloutText}>
                        {`Stage: ${anchor.zone.zone}`}
                    </text>
                    <text x="14" y="46" fontSize="10.5" fontFamily="'Roboto Mono', monospace" fill={colors.calloutText}>
                        {`Top/Bottom (MD): ${round(anchor.zone.topMD, 1)}/${round(anchor.zone.bottomMD, 1)} m`}
                    </text>
                    <text x="14" y="61" fontSize="10.5" fontFamily="'Roboto Mono', monospace" fill={colors.calloutText}>
                        {`Nr of perf.: ${anchor.zone.nrOfPerforation ?? 0}`}
                    </text>
                    <text x="14" y="76" fontSize="10.5" fontFamily="'Roboto Mono', monospace" fill={colors.calloutText}>
                        {`Perforation diameter: ${round(anchor.zone.perforationDiameter ?? 0, 3)} m`}
                    </text>
                </g>
            </g>
        )
    }

    return (
        <div ref={wrapperRef} style={{ width: '100%', height: compact ? 'auto' : '100%', position: 'relative' }}>
        {
            compact && (
                <Tooltip title={showAllDetails ? 'Hide details' : 'Show details'}>
                    <IconButton
                        size="small"
                        className={`wellboreDiagramToggle${showAllDetails ? ' wellboreDiagramToggle--on' : ''}`}
                        aria-pressed={showAllDetails}
                        aria-label={showAllDetails ? 'Hide details' : 'Show details'}
                        onClick={() => setShowAllDetails(v => !v)}
                    >
                        <FormatListBulletedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )
        }
        <svg
            ref={svgRef}
            width="100%"
            height={compact ? svgHeight : '100%'}
            viewBox={`0 0 ${width} ${svgHeight}`}
            style={{ display: 'block' }}
        >
            <defs>
                <linearGradient id="wellboreBg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={colors.bgFrom} />
                    <stop offset="100%" stopColor={colors.bgTo} />
                </linearGradient>
                <filter id="calloutShadow" x="-20%" y="-20%" width="140%" height="160%">
                    <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor={colors.shadowColor} floodOpacity={colors.shadowOpacity} />
                </filter>
            </defs>

            <rect x="0" y="0" width={width} height={svgHeight} fill="url(#wellboreBg)" />

            {
                depthTicks.map(tick => (
                    <g key={tick.value}>
                        <line
                            x1={paddingLeft - 8} y1={tick.y}
                            x2={width - 12} y2={tick.y}
                            stroke={colors.gridLine}
                            strokeWidth="1"
                            strokeDasharray="3 4"
                        />
                        <text x="4" y={tick.y + 3} fontSize="9.5" fontFamily="'Roboto Mono', monospace" fill={colors.gridText}>
                            {Math.round(tick.value)}
                        </text>
                    </g>
                ))
            }

            {
                segments.map(seg => (
                    <g key={seg.key}>
                        <line
                            x1={seg.x1} y1={seg.y1} x2={seg.x2} y2={seg.y2}
                            stroke={seg.baseColor}
                            strokeWidth={seg.strokeWidth}
                            strokeLinecap={seg.cap}
                        />
                        <line
                            x1={seg.hx1} y1={seg.hy1} x2={seg.hx2} y2={seg.hy2}
                            stroke={seg.highlightColor}
                            strokeWidth={Math.max(seg.strokeWidth * 0.28, 1.2)}
                            strokeLinecap={seg.cap}
                            opacity="0.85"
                        />
                    </g>
                ))
            }

            {
                // The very last piece may use a flat ("butt") cap when the active
                // stage's cutoff splits it — draw a matching round cap on top so the
                // pipe's tail end looks rounded, just like its start, without
                // reintroducing the split-boundary colour bulge that a round
                // strokeLinecap would cause on that piece.
                segments.length > 0 && (() => {
                    const tailSeg = segments[segments.length - 1]
                    return (
                        <circle
                            cx={tailSeg.x2} cy={tailSeg.y2} r={tailSeg.strokeWidth / 2}
                            fill={tailSeg.baseColor}
                        />
                    )
                })()
            }

            {
                fluidPieces.map(piece => (
                    <line
                        key={piece.key}
                        x1={piece.x1} y1={piece.y1} x2={piece.x2} y2={piece.y2}
                        stroke={piece.color}
                        strokeWidth={piece.strokeWidth}
                        strokeLinecap="butt"
                    />
                ))
            }

            {
                perforationMarks.map(mark => {
                    const perpAngle = mark.angle + Math.PI / 2
                    const dx = Math.cos(perpAngle) * mark.halfLength
                    const dy = Math.sin(perpAngle) * mark.halfLength
                    return (
                        <line
                            key={mark.key}
                            x1={mark.x - dx} y1={mark.y - dy}
                            x2={mark.x + dx} y2={mark.y + dy}
                            stroke={mark.color}
                            strokeWidth="2.5"
                            strokeLinecap="round"
                        />
                    )
                })
            }

            {
                points.map((point, idx) => (
                    <circle
                        key={idx}
                        cx={point.x} cy={point.y} r={4}
                        fill={colors.pointFill}
                        stroke={colors.pointStroke}
                        strokeWidth="1.5"
                    />
                ))
            }

            {
                compact
                    ? points.slice(0, -1).map((point, idx) => renderBadge(point, idx))
                    : points.slice(0, -1).map((point, idx) => renderCallout(point, idx))
            }

            {compact && perforationAnchors.map(anchor => renderPerforationBadge(anchor))}

            {!compact && perforationAnchors.map(anchor => renderPerforationCallout(anchor))}

            <g transform={`translate(${paddingLeft}, ${svgHeight - 24})`}>
                <line x1="0" y1="0" x2="18" y2="0" stroke={colors.tubingBase} strokeWidth="6" strokeLinecap="round" />
                <text x="24" y="4" fontSize="10" fontFamily="'Roboto Mono', monospace" fill={colors.legendText}>Tubing</text>
                {
                    isDataMode
                        ? [93, 99, 105].map(x => (
                            <line key={x} x1={x} y1="-5" x2={x} y2="5" stroke={colors.markUpcoming} strokeWidth="2.5" strokeLinecap="round" />
                        ))
                        : <line x1="90" y1="0" x2="108" y2="0" stroke={colors.perforatedBase} strokeWidth="6" strokeLinecap="round" />
                }
                <text x="114" y="4" fontSize="10" fontFamily="'Roboto Mono', monospace" fill={colors.legendText}>Perforated interval</text>
            </g>
        </svg>
        {compact && renderDetails()}
        </div>
    )
}

export default WellborePipeDiagram
